
import { useEffect, useRef, useState } from 'react'
import { Icon } from '@/components/ui/Icon'
import { btn, cx, notice } from '@/lib/ui'
import { errorMessage } from '@/lib/client-api'

type State = { token: string | null } | null

/**
 * The read-only link for this project.
 *
 * Showing a plan to a co-host, a venue or a hire company used to mean making
 * them a Member — who can then delete the workspace's projects — or sending a
 * PDF that is stale the moment the canvas changes. This is a link that stays
 * current and grants nothing else.
 */
export function ShareMenu({ apiBase, projectId }: { apiBase: string; projectId: string }) {
  const [open, setOpen] = useState(false)
  const [state, setState] = useState<State>(null)
  const [busy, setBusy] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [copied, setCopied] = useState(false)
  const panelRef = useRef<HTMLDivElement>(null)

  useEffect(() => {
    if (!open) return
    function onDown(event: MouseEvent) {
      if (!panelRef.current?.contains(event.target as Node)) setOpen(false)
    }
    function onKey(event: KeyboardEvent) {
      if (event.key === 'Escape') setOpen(false)
    }
    document.addEventListener('mousedown', onDown)
    document.addEventListener('keydown', onKey)
    return () => {
      document.removeEventListener('mousedown', onDown)
      document.removeEventListener('keydown', onKey)
    }
  }, [open])

  const url = state?.token ? `${window.location.origin}/share/${state.token}` : null

  async function call(method: 'POST' | 'DELETE') {
    setBusy(true)
    setError(null)
    try {
      const response = await fetch(`${apiBase}/projects/${projectId}/share`, { method })
      const body = await response.json()
      if (!response.ok) throw new Error(body.error ?? 'Could not change the link')
      setState({ token: body.shareToken ?? null })
    } catch (cause) {
      setError(errorMessage(cause, 'Could not change the link'))
    } finally {
      setBusy(false)
    }
  }

  return (
    <div className="relative">
      <button
        type="button"
        onClick={() => setOpen((current) => !current)}
        className={cx(btn.sm, btn.secondary)}
        title="Share a read-only link to this cable list"
        aria-expanded={open}
      >
        <Icon name="external" className="h-3.5 w-3.5" />
        <span className="sr-only 2xl:not-sr-only">Share</span>
      </button>

      {open && (
        <div
          ref={panelRef}
          role="dialog"
          aria-label="Share this project"
          className="panel absolute right-0 top-full z-40 mt-2 w-80 rounded-[var(--radius-card)] p-4 shadow-[var(--shadow-float)]"
        >
          <p className="text-sm font-semibold tracking-tight">Share a read-only link</p>
          <p className="mt-1 text-xs text-muted">
            Anyone with the link sees this project&rsquo;s cable list and power figure. They cannot open
            the canvas, the equipment list or anything else in this workspace.
          </p>

          {url ? (
            <>
              <input
                readOnly
                value={url}
                onFocus={(event) => event.currentTarget.select()}
                className="mt-3 w-full rounded-xl border border-[var(--border)] bg-surface/70 px-2.5 py-1.5 font-mono text-[11px]"
              />
              <div className="mt-2.5 flex flex-wrap gap-2">
                <button
                  type="button"
                  className={cx(btn.sm, btn.primary)}
                  onClick={async () => {
                    try {
                      await navigator.clipboard.writeText(url)
                      setCopied(true)
                      setTimeout(() => setCopied(false), 1600)
                    } catch {
                      setError('Could not copy. Select the link and copy it by hand.')
                    }
                  }}
                >
                  <Icon name={copied ? 'check' : 'copy'} className="h-3.5 w-3.5" />
                  {copied ? 'Copied' : 'Copy link'}
                </button>
                <button
                  type="button"
                  disabled={busy}
                  onClick={() => call('DELETE')}
                  className={cx(btn.sm, btn.ghost)}
                  title="Anyone still holding the old link loses access"
                >
                  Stop sharing
                </button>
              </div>
            </>
          ) : (
            <button
              type="button"
              disabled={busy}
              onClick={() => call('POST')}
              className={cx(btn.base, btn.primary, 'mt-3')}
            >
              {busy ? 'Creating…' : 'Create link'}
            </button>
          )}

          {error && <p role="alert" className={cx('mt-3', notice.error)}>{error}</p>}
        </div>
      )}
    </div>
  )
}
